import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Tag,
  Badge,
  Divider,
  Card
} from "antd";
import {
  FaRegUserCircle,
  FaKey,
  FaBuilding,
  FaMapMarkerAlt,
  FaEnvelope,
  FaIdCard,
  FaCalendarAlt
} from "react-icons/fa";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const UserModals = ({
  roles,
  selectedUser,
  setSelectedUser,
  isCreateModalVisible,
  setIsCreateModalVisible,
  isEditModalVisible,
  setIsEditModalVisible,
  isViewModalVisible,
  setIsViewModalVisible,
  isDeleteModalVisible,
  setIsDeleteModalVisible,
  is2FAModalVisible,
  setIs2FAModalVisible,
  createForm,
  editForm,
  handleCreateUser,
  handleUpdateUser,
  handleDeleteUser,
  handleToggle2FA,
  handleGenerateBackupCodes
}) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  // Populate edit form when modal opens
  useEffect(() => {
    if (isEditModalVisible && selectedUser) {
      const formData = { ...selectedUser };
      // Ensure role field holds role ID in the form
      if (selectedUser.role && typeof selectedUser.role === 'string') {
        const roleObj = roles.find(r => r._id === selectedUser.role || r.name === selectedUser.role);
        if (roleObj) {
          formData.role = roleObj._id;
        }
      }
      // Do NOT set password fields from selectedUser for security.
      editForm.setFieldsValue(formData);
      // explicitly clear password fields
      editForm.setFieldsValue({ password: undefined, confirmPassword: undefined });
    }
  }, [isEditModalVisible, selectedUser, editForm, roles]);

  return (
    <>
      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input prefix={<FaRegUserCircle />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="techId"
                label="Tech ID"
                rules={[{ required: true, message: 'Please enter tech ID' }]}
              >
                <Input prefix={<FaIdCard />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input prefix={<FaEnvelope />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter password' }]}
              >
                <Input.Password prefix={<FaKey />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supervisor"
                label="Supervisor"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
              >
                <Input prefix={<FaBuilding />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: "Please select role" }]}
              >
                <Select placeholder="Select a role">
                  {roles.map((role) => (
                    <Option key={role._id} value={role._id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="loginAccess"
                label="Login Access"
                rules={[{ required: true, message: "Please select login access" }]}
              >
                <Select placeholder="Select a login access" defaultValue="both">
                    <Option value="Web">Web</Option>
                    <Option value="Mobile">Mobile</Option>
                    <Option value="Both">Both</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>  
          <Form.Item
            name="billaddress"
            label="Billing Address"
          >
            <TextArea prefix={<FaMapMarkerAlt />} rows={3} />
          </Form.Item>

          <Form.Item
            name="netsuite_id"
            label="NetSuite ID"
          >
            <Input />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
              <Button onClick={() => {
                setIsCreateModalVisible(false);
                createForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={values => {
            // If password fields are empty/undefined, remove them from payload
            const payload = { ...values };
            if (!payload.password) delete payload.password;
            delete payload.confirmPassword; // never send confirm field
            handleUpdateUser(payload);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input prefix={<FaRegUserCircle />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="techId"
                label="Tech ID"
                rules={[{ required: true, message: 'Please enter tech ID' }]}
              >
                <Input prefix={<FaIdCard />} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                {/* 🔒 Disabled email field in Edit form */}
                <Input prefix={<FaEnvelope />} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: 'Please select role' }]}
              >
                <Select placeholder="Select a role">
                  {roles.map((role) => (
                    <Option key={role._id} value={role._id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Password fields (optional) */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                extra="Leave blank to keep current password"
                rules={[
                  // only validate min length if user entered a password
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      if (value.length < 6) {
                        return Promise.reject(new Error('Password must be at least 6 characters'));
                      }
                      return Promise.resolve();
                    },
                  })
                ]}
              >
                <Input.Password prefix={<FaKey />} placeholder="Enter new password (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const pw = getFieldValue('password');
                      if (!pw && !value) {
                        // both empty - fine
                        return Promise.resolve();
                      }
                      if (pw && !value) {
                        return Promise.reject(new Error('Please confirm the password'));
                      }
                      if (pw && value !== pw) {
                        return Promise.reject(new Error('Passwords do not match'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <Input.Password prefix={<FaKey />} placeholder="Confirm new password" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supervisor"
                label="Supervisor"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
              >
                <Input prefix={<FaBuilding />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="loginAccess"
                label="Login Access"
                rules={[{ required: true, message: "Please select login access" }]}
              >
                <Select placeholder="Select a login access" defaultValue="both">
                  <Option value="Web">Web</Option>
                  <Option value="Mobile">Mobile</Option>
                  <Option value="Both">Both</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="netsuite_id"
                label="NetSuite ID"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="billaddress"
            label="Billing Address"
          >
            <TextArea prefix={<FaMapMarkerAlt />} rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update User
              </Button>
              <Button onClick={() => {
                setIsEditModalVisible(false);
                editForm.resetFields();
                setSelectedUser(null);
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View User Details Modal */}
      <Modal
        title="User Details"
        open={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          setSelectedUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsViewModalVisible(false);
            setSelectedUser(null);
          }}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedUser && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div className="d-flex align-items-center">
                      <FaRegUserCircle size={24} style={{ marginRight: '10px' }} />
                      <Title level={4} style={{ margin: 0 }}>{selectedUser.name}</Title>
                    </div>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong><FaEnvelope /> Email:</Text>
                        <br />
                        <Text>{selectedUser.email}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong><FaIdCard /> Tech ID:</Text>
                        <br />
                        <Text>{selectedUser.techId}</Text>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong><FaBuilding /> Department:</Text>
                        <br />
                        <Text>{selectedUser.department || 'N/A'}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>Title:</Text>
                        <br />
                        <Text>{selectedUser.title || 'N/A'}</Text>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong>Supervisor:</Text>
                        <br />
                        <Text>{selectedUser.supervisor || 'N/A'}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>Role:</Text>
                        <br />
                        <Tag color={selectedUser.role === "admin" ? "red" : selectedUser.role === "tech" ? "blue" : "green"}>
                          {selectedUser.role || "user"}
                        </Tag>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong><FaMapMarkerAlt /> Billing Address:</Text>
                        <br />
                        <Text>{selectedUser.billaddress || 'N/A'}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>NetSuite ID:</Text>
                        <br />
                        <Text>{selectedUser.netsuite_id || 'N/A'}</Text>
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong>2FA Status:</Text>
                        <br />
                        <Badge
                          status={selectedUser.twoFactorEnabled ? "success" : "default"}
                          text={selectedUser.twoFactorEnabled ? "Enabled" : "Disabled"}
                        />
                      </Col>
                      <Col span={12}>
                        <Text strong>Verification Status:</Text>
                        <br />
                        <Tag color={selectedUser.IsVerified ? "green" : "red"}>
                          {selectedUser.IsVerified ? "Verified" : "Unverified"}
                        </Tag>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong>Next Orders:</Text>
                        <br />
                        {selectedUser.nextOrders && selectedUser.nextOrders.length > 0 ? (
                          selectedUser.nextOrders.map((order, index) => (
                            <Tag key={index} color="blue">#{order.tranId}</Tag>
                          ))
                        ) : (
                          <Text>No Orders</Text>
                        )}
                      </Col>
                      <Col span={12}>
                        <Text strong>Hide Completed Orders:</Text>
                        <br />
                        <Tag color={selectedUser.hideCompletedOrder ? "red" : "green"}>
                          {selectedUser.hideCompletedOrder ? "Yes" : "No"}
                        </Tag>
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Text strong><FaCalendarAlt /> Created:</Text>
                        <br />
                        <Text>{new Date(selectedUser.created_at).toLocaleDateString()}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>Last Updated:</Text>
                        <br />
                        <Text>{new Date(selectedUser.updated_at).toLocaleDateString()}</Text>
                      </Col>
                    </Row>
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* 2FA Management Modal */}
      <Modal
        title="2FA Management"
        open={is2FAModalVisible}
        onCancel={() => {
          setIs2FAModalVisible(false);
          setSelectedUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIs2FAModalVisible(false);
            setSelectedUser(null);
          }}>
            Close
          </Button>
        ]}
        width={500}
      >
        {selectedUser && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong>Current 2FA Status:</Text>
                <br />
                <Badge
                  status={selectedUser.twoFactorEnabled ? "success" : "default"}
                  text={selectedUser.twoFactorEnabled ? "Enabled" : "Disabled"}
                />
              </div>

              <div>
                <Text strong>Backup Codes:</Text>
                <br />
                <Text>
                  {selectedUser.twoFactorBackupCodes && selectedUser.twoFactorBackupCodes.length > 0
                    ? `${selectedUser.twoFactorBackupCodes.length} codes available`
                    : "No backup codes available"
                  }
                </Text>
              </div>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                {selectedUser.twoFactorEnabled && (
                  <Button
                    type="primary"
                    danger
                    onClick={() => handleToggle2FA(false)}
                    style={{ width: '100%' }}
                  >
                    Disable 2FA
                  </Button>
                )}

                {selectedUser.twoFactorEnabled && (
                  <Button
                    type="default"
                    onClick={handleGenerateBackupCodes}
                    style={{ width: '100%' }}
                  >
                    Generate New Backup Codes
                  </Button>
                )}
              </Space>
            </Space>
          </div>
        )}
      </Modal>

      {/* Delete/Restore Confirmation Modal */}
      <Modal
        title={selectedUser?.isDeleted ? "Restore User" : "Delete User"}
        open={isDeleteModalVisible}
        afterClose={() => setDeleteConfirmation('')}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedUser(null);
          setDeleteConfirmation('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsDeleteModalVisible(false);
            setSelectedUser(null);
            setDeleteConfirmation('');
          }}>
            Cancel
          </Button>,
          <Button 
            key="action" 
            type="primary" 
            danger={!selectedUser?.isDeleted}
            onClick={handleDeleteUser}
            disabled={selectedUser?.isDeleted ? deleteConfirmation !== 'RESTORE' : deleteConfirmation !== 'DELETE'}
          >
            {selectedUser?.isDeleted ? "Restore User" : "Delete User"}
          </Button>
        ]}
      >
        {selectedUser && (
          <div>
            <Text>
              Are you sure you want to {selectedUser.isDeleted ? 'restore' : 'delete'} user <strong>{selectedUser.name}</strong>?
            </Text>
            <br />
            <Text strong>
              To confirm {selectedUser.isDeleted ? 'restoration' : 'deletion'}, please type <Text code>{selectedUser.isDeleted ? 'RESTORE' : 'DELETE'}</Text> in the field below:
            </Text>
            <br />
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={`Type ${selectedUser.isDeleted ? 'RESTORE' : 'DELETE'} to confirm`}
              style={{ marginTop: '8px' }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default UserModals;

